import {
  ConditionGroupLogic,
  isConditionGroup,
  isRelatedResourceCondition,
  type ConditionGroup,
  type ConditionGroupItem,
  type FieldCondition,
  type RelatedResourceCondition,
  type SegmentConditionGroups,
} from '@reach/shared-types/conditions';
import { generateId } from '@/lib/generate-id';
import type { FieldInfo, SegmentSchemaCatalog } from '../../engage-segments-tab/types';
import { findRelationshipForStoredCondition } from '../../engage-segments-tab/segment-definitions-api';
import type { BuilderFieldType } from './segment-builder.types';
import {
  isBuilderNestedGroup,
  isBuilderRelatedResourceCondition,
  type BuilderCondition,
  type BuilderFieldCondition,
  type BuilderGroup,
  type BuilderNestedGroup,
  type BuilderRelatedResourceCondition,
  type BuilderState,
} from './segment-builder.types';

export type BuilderStateConversionResult =
  | { kind: 'supported'; state: BuilderState }
  | { kind: 'unsupported'; reason: string };

const NESTED_RELATED_RESOURCE_UNSUPPORTED_REASON =
  'This segment uses nested related-resource conditions, which the visual builder cannot edit yet.';

/** Spread a description key only when it has a value, keeping stored JSON clean. */
function withDescription(description?: string): { description?: string } {
  return description ? { description } : {};
}

function fieldTypeFromFields(fieldName: string, fields: FieldInfo[]): BuilderFieldType {
  const info = fields.find(field => field.name === fieldName);
  return info?.type ?? 'unknown';
}

/**
 * Recursively convert a condition group item (API format) to a builder condition.
 */
function conditionItemToBuilder(
  item: ConditionGroupItem,
  schemaCatalog: SegmentSchemaCatalog,
): BuilderCondition {
  // Nested condition group -> BuilderNestedGroup
  if (isConditionGroup(item)) {
    const nestedConditions = item.conditions.map(child => conditionItemToBuilder(child, schemaCatalog));
    return {
      id: generateId(),
      type: 'nested_group',
      logic: item.logic === ConditionGroupLogic.OR ? 'or' : 'and',
      conditions: nestedConditions,
      isExpanded: true,
      ...withDescription(item.description),
    } satisfies BuilderNestedGroup;
  }

  // Read stored relationship fields as-is; never re-derive them from discovery.
  if (isRelatedResourceCondition(item)) {
    const schema = schemaCatalog.schemas.find(s => s.id === item.relatedResource.schemaId);
    const relationship = schemaCatalog.contactSchemaId
      ? findRelationshipForStoredCondition({
          graph: schemaCatalog,
          subjectSchemaId: schemaCatalog.contactSchemaId,
          storedRelatedResource: item.relatedResource,
        })
      : undefined;
    const builderNested: BuilderFieldCondition[] = item.conditions.map(condition => {
      const fieldCondition = condition as FieldCondition;
      return {
        id: generateId(),
        type: 'field',
        field: fieldCondition.field,
        fieldType: fieldTypeFromFields(fieldCondition.field, schema?.fields ?? []),
        operator: fieldCondition.operator,
        value: [...fieldCondition.value],
        ...withDescription(fieldCondition.description),
      };
    });
    return {
      id: generateId(),
      type: 'related_resource',
      schemaId: item.relatedResource.schemaId,
      schemaName: schema?.name ?? 'Related resource',
      foreignKeyPath: item.relatedResource.foreignKeyPath,
      ...(item.relatedResource.foreignKeyOn ? { foreignKeyOn: item.relatedResource.foreignKeyOn } : {}),
      ...(relationship ? {} : { unavailableReason: 'This relationship is no longer available.' }),
      countOperator: item.countOperator,
      countValue: item.countValue,
      conditions: builderNested,
      isExpanded: true,
      ...withDescription(item.description),
    } satisfies BuilderRelatedResourceCondition;
  }

  // Field condition -> BuilderFieldCondition
  const fieldCond = item as FieldCondition;
  const fieldName = typeof fieldCond.field === 'string' ? fieldCond.field : fieldCond.field;
  return {
    id: generateId(),
    type: 'field',
    field: fieldName,
    fieldType: fieldTypeFromFields(fieldName, schemaCatalog.contactFields),
    operator: fieldCond.operator,
    value: [...fieldCond.value],
    ...withDescription(fieldCond.description),
  } satisfies BuilderFieldCondition;
}

function containsNestedRelatedResourceCondition(items: ConditionGroupItem[]): boolean {
  return items.some(item => {
    if (isConditionGroup(item)) {
      return containsNestedRelatedResourceCondition(item.conditions);
    }
    return isRelatedResourceCondition(item) && item.conditions.some(isRelatedResourceCondition);
  });
}

/**
 * Convert segment condition groups (API format) to builder state.
 * Fully supports nested condition groups via recursion.
 */
export function conditionGroupsToBuilderState(
  groups: SegmentConditionGroups,
  schemaCatalog: SegmentSchemaCatalog,
): BuilderStateConversionResult {
  if (groups.some(group => containsNestedRelatedResourceCondition(group.conditions))) {
    return { kind: 'unsupported', reason: NESTED_RELATED_RESOURCE_UNSUPPORTED_REASON };
  }
  const builderGroups: BuilderGroup[] = groups.map((group: ConditionGroup) => {
    const conditions = group.conditions.map(cond => conditionItemToBuilder(cond, schemaCatalog));
    return {
      id: generateId(),
      logic: group.logic === ConditionGroupLogic.OR ? 'or' : 'and',
      conditions,
      ...withDescription(group.description),
    };
  });

  return { kind: 'supported', state: { groups: builderGroups } };
}

/**
 * Recursively convert a builder condition to API format.
 */
function builderConditionToApi(cond: BuilderCondition): ConditionGroupItem {
  // Nested group -> ConditionGroup
  if (isBuilderNestedGroup(cond)) {
    const nestedConditions = cond.conditions.map(builderConditionToApi);
    return {
      type: 'group',
      logic: cond.logic === 'or' ? ConditionGroupLogic.OR : ConditionGroupLogic.AND,
      conditions: nestedConditions,
      ...withDescription(cond.description),
    } as ConditionGroup;
  }

  // Omit foreignKeyOn when absent so existing segments round-trip identically.
  if (isBuilderRelatedResourceCondition(cond)) {
    return {
      type: 'related_resource',
      relatedResource: {
        schemaId: cond.schemaId,
        foreignKeyPath: cond.foreignKeyPath,
        ...(cond.foreignKeyOn ? { foreignKeyOn: cond.foreignKeyOn } : {}),
      },
      countOperator: cond.countOperator,
      countValue: cond.countValue,
      conditions: cond.conditions.map(fc => ({
        type: 'property' as const,
        field: fc.field,
        operator: fc.operator,
        value: [...fc.value],
        ...withDescription(fc.description),
      })),
      ...withDescription(cond.description),
    } satisfies RelatedResourceCondition;
  }

  // Field condition -> FieldCondition (type discriminant for condition group items)
  return {
    type: 'property',
    field: cond.field,
    operator: cond.operator,
    value: [...cond.value],
    ...withDescription(cond.description),
  } as FieldCondition;
}

/**
 * Convert builder state to segment condition groups (API format).
 * Fully supports nested condition groups via recursion.
 */
export function builderStateToConditionGroups(state: BuilderState): SegmentConditionGroups {
  return state.groups.map(group => {
    const conditions = group.conditions.map(builderConditionToApi);
    return {
      type: 'group' as const,
      logic: group.logic === 'or' ? ConditionGroupLogic.OR : ConditionGroupLogic.AND,
      conditions,
      ...withDescription(group.description),
    };
  });
}
